import { useEffect, useState } from 'react';

/**
 * Debounce a fast-changing value (typically a search box) so dependents —
 * an API fetch, an expensive filter — only react once the user pauses.
 *
 * @example
 * const [search, setSearch] = useState('');
 * const debouncedSearch = useDebouncedValue(search.trim());
 * useEffect(() => { fetchRows(debouncedSearch); }, [debouncedSearch]);
 *
 * @param value - The live value to debounce
 * @param delayMs - Quiet period before the value is passed on (default 300ms)
 * @returns The value as it stood `delayMs` after its last change
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
